
import React from 'react';

export type ParentTabType = 'launchpad' | 'intelligence' | 'operations' | 'collaboration';
export type ChildTabType = 'gaps' | 'math' | 'sites' | 'risk' | 'strategy';

interface TabConfig {
  id: string;
  label: string;
}

const PARENT_TABS: TabConfig[] = [
  { id: 'launchpad', label: 'Launch Pad' },
  { id: 'intelligence', label: 'Intelligence Hub' },
  { id: 'operations', label: 'Operations Hub' },
  { id: 'collaboration', label: 'Collaboration Hub' }
];

const CHILD_TABS: TabConfig[] = [
  { id: 'gaps', label: 'Market Gap' },
  { id: 'math', label: 'Launch Math' },
  { id: 'sites', label: 'Site Recon' },
  { id: 'risk', label: 'Risk Guard' },
  { id: 'strategy', label: '18-Month Strategy' }
];

interface NavigationSuiteProps {
  activeParentTab: ParentTabType;
  setActiveParentTab: (tab: ParentTabType) => void;
  activeChildTab: ChildTabType;
  setActiveChildTab: (tab: ChildTabType) => void;
  subscriptionTier: string;
}

const NavigationSuite: React.FC<NavigationSuiteProps> = ({
  activeParentTab,
  setActiveParentTab,
  activeChildTab,
  setActiveChildTab,
  subscriptionTier
}) => {
  const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';
  const isPremiumPro = subscriptionTier === 'premium_pro';

  const isTabLocked = (tabId: string) => {
    if (tabId === 'intelligence' || tabId === 'operations') return !isPremium;
    if (tabId === 'collaboration') return !isPremiumPro;
    // Child tabs in Launchpad
    if (['risk', 'math', 'sites', 'strategy'].includes(tabId)) return !isPremium;
    return false;
  };

  return (
    <>
      {/* Parent Navigation Tabs */}
      <nav className="relative z-50 bg-[#322D29] border-b border-white/[0.06] overflow-x-auto no-scrollbar">
        <div className="max-w-4xl mx-auto px-8 flex justify-between min-w-max gap-8">
          {PARENT_TABS.map((tab) => {
            if (!tab || !tab.id) return null;
            const locked = isTabLocked(tab.id);
            return (
            <button 
              key={`parent-tab-${tab.id}`} 
              onClick={() => setActiveParentTab(tab.id as ParentTabType)} 
              className={`py-4 relative text-[10px] font-bold uppercase tracking-[0.25em] font-heading transition-all shrink-0 flex items-center gap-2 cursor-pointer ${activeParentTab === tab.id ? 'text-white' : 'text-white/60 hover:text-white'}`}
            >
              {tab.label}
              {locked && <i className="fas fa-lock text-[8px] text-white/40"></i>}
              {activeParentTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F26522]"></div>
              )}
            </button>
          )})}
        </div>
      </nav>

      {/* Child Navigation Tabs (Only for Launch Pad) */}
      {activeParentTab === 'launchpad' && (
        <nav className="relative z-40 bg-[#3a3430] border-b border-white/[0.06] overflow-x-auto no-scrollbar">
          <div className="max-w-4xl mx-auto px-8 flex gap-6 min-w-max">
            {CHILD_TABS.map((tab) => {
              if (!tab || !tab.id) return null;
              const locked = isTabLocked(tab.id);
              return (
              <button 
                key={`child-tab-${tab.id}`} 
                onClick={() => setActiveChildTab(tab.id as ChildTabType)} 
                className={`py-3 relative text-[8px] font-bold uppercase tracking-[0.2em] font-heading transition-all shrink-0 flex items-center gap-2 cursor-pointer ${activeChildTab === tab.id ? 'text-[#F26522]' : 'text-white/60 hover:text-white'}`}
              >
                {tab.label}
                {locked && <i className="fas fa-lock text-[7px] text-white/40"></i>}
                {activeChildTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F26522]"></div>
                )}
              </button>
            )})}
          </div>
        </nav>
      )}
    </>
  );
};

export default NavigationSuite;
